import { Checkbox, Divider, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useCallback, useMemo, useState, useEffect } from 'react';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import AccordionApplicantForm from '../applicant/form';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import Table from 'shell/components/shared/table';
import { PersonVM } from 'shell/view-models/search-person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PmiButton } from 'shell/components/shared/button';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerPersonGeneralityList } from '../generality/transformer-anpr';
import { headCells } from '../generality/form';
import { noop } from 'shell/utils/noop';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ErrorModal from 'shell/components/shared/anomalie-modal';

interface AccordionAccountholderFormProps {
  disabled?: boolean;
  useIntern?: boolean;
  values?: AccordionAccountholderFormVM;
  errors?: FormikErrors<AccordionAccountholderFormVM>;
  isForAttestazione?: boolean;
  touched?: FormikTouched<AccordionAccountholderFormVM>;
  onChangeValues: (values: AccordionAccountholderFormVM) => void;
  onChange: (
    key: keyof AccordionAccountholderFormVM,
    value: AccordionAccountholderFormVM[keyof AccordionAccountholderFormVM]
  ) => void;
  visibilityTable: boolean;
  setVisibilityTable: (visibility: boolean) => void;
  isDeclaration?: boolean;
  url?: string;
}

const AccordionAccountholderFormComponent = ({
  disabled,
  useIntern = false,
  values,
  onChange,
  onChangeValues,
  isDeclaration,
  isForAttestazione,
  errors,
  touched,
  visibilityTable,
  setVisibilityTable,
  url = `${__PERSON_NEW_API_URL__}/persona`,
}: AccordionAccountholderFormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const formContext = useFormikContext<CertificateFormVM>();
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const handleSameApplicant = useEventCallback(() => {
    if (formContext?.values?.certificateInternOnly) {
      return;
    }

    if (!values?.sameApplicant) {
      onChange('sameApplicant', !values?.sameApplicant);
      return;
    }

    onChangeValues({ sameApplicant: !values?.sameApplicant, account: {} });
  });

  // Handle accountholder
  const onFieldApplicantChange = useEventCallback(
    (
      key: keyof AccordionApplicantFormVM,
      value: AccordionApplicantFormVM[keyof AccordionApplicantFormVM]
    ) => {
      onChange('account', {
        ...(values?.account || {}),
        applicant: {
          ...(values?.account?.applicant || {}),
          [key]: value,
        },
      });
    }
  );

  // Handle accountholder
  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>(
    url,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>(url, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerPersonGeneralityList,
  });

  const showTooltip = useMemo(() => {
    if (
      values?.account?.applicant?.fiscalCode ||
      (values?.account?.applicant?.name && values?.account?.applicant?.surname)
    ) {
      if (errors?.searchAccountholder) {
        return true;
      }
      return false;
    }

    if (
      !values?.account?.applicant?.fiscalCode ||
      (!values?.account?.applicant?.name && !values?.account?.applicant?.surname)
    ) {
      return true;
    }

    return false;
  }, [values, errors?.searchAccountholder]);

  const tooltipMessage = useMemo(() => {
    if (
      values?.account?.applicant?.fiscalCode ||
      (values?.account?.applicant?.name && values?.account?.applicant?.surname)
    ) {
      if (errors?.searchAccountholder) {
        return errors?.searchAccountholder;
      }
      return;
    }
    if (
      !values?.account?.applicant?.fiscalCode ||
      (!values?.account?.applicant?.name && !values?.account?.applicant?.surname)
    ) {
      return 'Inserire codice fiscale o cognome e nome';
    }
  }, [values, errors?.searchAccountholder]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);
    formContext.setFieldValue('isClearButtonClicked', false);

    if (
      !values?.account?.applicant?.fiscalCode &&
      !(values?.account?.applicant?.name && values?.account?.applicant?.surname)
    ) {
      return;
    }

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: values?.account?.applicant?.fiscalCode,
        name: values?.account?.applicant?.name,
        surname: values?.account?.applicant?.surname,
        withoutName: values?.account?.applicant?.withoutName,
        withoutSurname: values?.account?.applicant?.withoutSurname,
        gender: values?.account?.applicant?.gender,
        birthday: values?.account?.applicant?.birthDate,
        birthdayChoice: values?.account?.applicant?.birthChoiceDate,
        // birthday: values?.account?.generality?.birthDate,
        // birthdayChoice: values?.account?.generality?.birthChoiceDate,
        // isForeign: values?.account?.generality?.isForeign,
        // foreignMunicipality: values?.account?.generality?.foreignMunicipality,
        // foreignStateBirth: values?.account?.generality?.foreignStateBirth,
        // municipalityOfBirth: values?.account?.generality?.municipalityOfBirth,
        // provinceOfBirth:
        //   values?.account?.generality?.municipalityOfBirth?.province || undefined,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
      identityCard: {
        // number: values?.account?.generality?.numberDocument,
        // TODO data rilascio
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificateFormVM>;

      if (castedResponse?.errors?.length > 0) {
        setVisibility({});
        return;
      }

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        if (element.anomaly?.length && !isDeclaration) {
          setOpenErrorModal(true);
        }

        formContext.setFieldValue('isBritish', element.isBritish);

        onChangeValues({
          isAire: element.isAire,
          isLocked: values?.isLocked,
          searchAccountholder: true,
          certifiableAccountholder: element.certifiableAccountholder,
          anomaly: element.anomaly,
          account: {
            applicant: element.applicant,
            generality: element.generality,
          },
        });
        return;
      }
      setVisibilityTable(true);
      resetPagination();
    });
  });

  const handleClickSelect = useEventCallback((person: CertificateFormVM) => {
    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person.applicant?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person?.generality?.idSubjectAnpr,
          },
          datiRichiesta: {
            comuneResidenza:
              userProfile?.datiLogin?.profile?.feature?.anpr === '1'
                ? {
                    codiceIstat: userProfile?.datiLogin?.codiceIstat,
                    nomeComune: userProfile?.datiLogin?.nomeComune,
                    siglaProvinciaIstat: userProfile?.datiLogin?.siglaProvincia,
                  }
                : undefined,
            ricercaNazionale: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? '1' : '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificateFormVM>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        if (element.anomaly?.length && !isDeclaration) {
          setOpenErrorModal(true);
        }
        onChangeValues({
          isAire: element.isAire,
          isLocked: values?.isLocked,
          searchAccountholder: true,
          certifiableAccountholder: element.certifiableAccountholder,
          anomaly: element.anomaly,
          account: {
            applicant: element.applicant,
            generality: element.generality,
          },
        });
        setVisibilityTable(false);
        resetPagination();
      }
    });
  });

  const castedAnprData = anpr.data as AnprVM<CertificateFormVM>;

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.applicant?.fiscalCode || '',
        surname: person.applicant?.surname || '',
        name: person.applicant?.name || '',
        gender: person.applicant?.gender || '',
        birthday: person.generality?.birthDate?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.generality?.birthplace,
        residence: person.generality?.residenceString,
        state: person.generality?.state,
        anpr: person.generality?.idSubjectAnpr,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons]);

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const accountErrors = errors?.account as FormikErrors<{
    applicant?: FormikErrors<AccordionApplicantFormVM>;
  }>;

  const accountTouched = touched?.account as unknown as FormikTouched<{
    applicant?: FormikTouched<AccordionApplicantFormVM>;
  }>;

  useEffect(() => {
    if (formContext?.values?.isClearButtonClicked) {
      setVisibility({
        error: false,
        warning: false,
      });
    }
  }, [formContext?.values?.isClearButtonClicked]);

  return (
    <Fragment>
      <Grid container spacing={0} columnSpacing={1} rowSpacing={0}>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="sameApplicant"
                checked={values?.sameApplicant || false}
                onChange={handleSameApplicant}
                inputProps={{ 'aria-label': "il richiedente coincide con l'intestatario" }}
                disabled={useIntern ? true : disabled || values?.isLocked}
              />
            }
            label="Il richiedente coincide con l'intestatario"
          />
        </Grid>
        {!values?.sameApplicant ? (
          <Fragment>
            {visibilityTable || errorMessage.visibility ? (
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Error}
                  title={errorMessage.title || ''}
                  messages={errorMessage.messages || []}
                  visibility={errorMessage.visibility || false}
                  onChangeVisibility={onVisibility}
                />
                <SearchMessages
                  type={MessageType.Warning}
                  title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
                  messages={castedAnprData?.warnings || []}
                  visibility={
                    visibility.warning !== undefined
                      ? visibility.warning
                      : (castedAnprData?.warnings || [])?.length > 0
                  }
                  onChangeVisibility={onVisibility}
                />
              </Grid>
            ) : undefined}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <AccordionApplicantForm
                isForAttestazione={isForAttestazione}
                disabled={useIntern ? false : disabled}
                values={values?.account?.applicant}
                generality={values?.account?.generality}
                onChange={onFieldApplicantChange}
                errors={accountErrors?.applicant as FormikErrors<AccordionApplicantFormVM>}
                touched={
                  accountTouched?.applicant as unknown as FormikTouched<AccordionApplicantFormVM>
                }
              />
            </Grid>
            <Grid container spacing={2} item xs={12} display="flex" justifyContent="flex-end">
              <Grid item xs={12} md={4}>
                <PmiTooltipError show={showTooltip} message={tooltipMessage}>
                  <PmiButton
                    color="cdmBrandThird"
                    variant="outlined"
                    onClick={handleSearch}
                    fullWidth
                    id="handle-search-anpr"
                  >
                    Cerca
                  </PmiButton>
                </PmiTooltipError>
              </Grid>
            </Grid>
            {dataPersons.length > 1 && visibilityTable ? (
              <Grid item xs={12}>
                <Table
                  ariaLabel="Lista Persone"
                  page={paginationState.page}
                  rowsPerPage={paginationState.rowsPerPage}
                  body={dataPersons}
                  count={dataPersons.length}
                  head={headCells}
                  onSort={noop}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  onVisibilityPress={noop}
                  orders={{}}
                  isNotLayoutFixed
                  noSort
                  hideButtons
                />
              </Grid>
            ) : undefined}
          </Fragment>
        ) : undefined}
      </Grid>
      <ErrorModal
        open={openErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
        dataPersons={values?.anomaly}
      />
    </Fragment>
  );
};

AccordionAccountholderFormComponent.displayName = 'Accordion accountholder form component';

const AccordionAccountholderForm = memo(AccordionAccountholderFormComponent);

export default AccordionAccountholderForm;
